import { z } from 'zod';

import { createOffsetPaginationRequestQuerySchema } from './pagination';
import { PrimaryKeySchema, commaDelimitedList } from './utils';

/**
 * A source is a configuration that points to a single "document", documents are defined as a webpage, file, etc..., it should not point to a collection of documents
 */
export const SourceSchema = z.object({
  sourceId: PrimaryKeySchema,
  title: z.string(),
  description: z.string().nullable(),
  url: z.string().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

export type Source = z.infer<typeof SourceSchema>;

export const SourcesSchema = z.array(SourceSchema);

export type Sources = z.infer<typeof SourcesSchema>;

export const SourcesBaseQuerySchema = z.object({
  titleLike: z.string().optional(),
  sourceIds: commaDelimitedList(PrimaryKeySchema.array()).optional(),
  excludeSourceIds: commaDelimitedList(PrimaryKeySchema.array()).optional(),
  projectIds: commaDelimitedList(PrimaryKeySchema.array()).optional(),
  availableForUse: z
    .boolean()
    .optional()
    .describe(
      'Only return sources that are available for runtime inference, this is defined as the source having at least 1 text chunk embedding available',
    ),
  collectionIds: commaDelimitedList(PrimaryKeySchema.array()).optional(),
  agentRunIds: commaDelimitedList(PrimaryKeySchema.array()).optional(),
  urls: commaDelimitedList(z.string().array()).optional(),
});

export const SourcesQuerySchema = createOffsetPaginationRequestQuerySchema(
  z.enum(['title', 'description', 'createdAt', 'updatedAt']),
).merge(SourcesBaseQuerySchema);

export type SourcesQuery = z.infer<typeof SourcesQuerySchema>;

export const CreateSourceSchema = SourceSchema.pick({
  title: true,
  description: true,
  url: true,
});

export type CreateSource = z.infer<typeof CreateSourceSchema>;

export const SourcesSearchQuerySchema = SourcesQuerySchema.extend({
  query: z.string(),
  limit: z.coerce.number().default(100).optional(),
  strategies: commaDelimitedList(
    z.enum(['semanticTitle', 'semanticEntities']).array().min(1),
  ).optional(),
}).omit({
  page: true,
  pageSize: true,
  orderBy: true,
  orderDirection: true,
});

export type SourcesSearchQuery = z.infer<typeof SourcesSearchQuerySchema>;

export const SourcesSearchResultSchema = z.object({
  sourceId: z.string(),
  score: z.number(),
});

export type SourcesSearchResult = z.infer<typeof SourcesSearchResultSchema>;

export const SourcesSearchResultsSchema = z.array(SourcesSearchResultSchema);

export type SourcesSearchResults = z.infer<typeof SourcesSearchResultsSchema>;

export const UpdateSourceSchema = z.object({
  shouldProcess: z.boolean().optional(),
});

export type UpdateSource = z.infer<typeof UpdateSourceSchema>;
